import React, { useState, useEffect, useMemo, useCallback } from "react";
import "../styles/ResultModal.css";

const CONFETTI_COLORS = ["#f0d060","#c89b3c","#ff8080","#80ffaa","#80c8ff","#d080ff"];
const MAX_ATTEMPTS = 5;

function buildShareText({ isWin, attemptNumber, gameMode, gameName, answers }) {
  const modeLabel = gameMode === "retail" ? "Retail" : "Classic";
  const nameLabel = gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Azerodle";
  const header = `Azerodle ${nameLabel} (${modeLabel}) — ${isWin ? attemptNumber : "X"}/${MAX_ATTEMPTS}`;
  const grid = answers.map(a => a.correct ? "🟨" : a.skipped ? "⬜" : "🟥").join("");
  return `${header}\n${grid}\n\nhttps://azerodle.com`;
}

function StatCard({ value, label }) {
  return (
    <div className="rm-stat-card">
      <span className="rm-stat-value">{value ?? "—"}</span>
      <span className="rm-stat-label">{label}</span>
    </div>
  );
}

function DistributionBar({ attempt, count, maxCount, isActive, isWin }) {
  const pct = maxCount > 0 ? Math.max((count / maxCount) * 100, 8) : 8;
  return (
    <div className="rm-dist-row">
      <span className="rm-dist-num">{attempt}</span>
      <div className="rm-dist-track">
        <div className={`rm-dist-fill ${isActive ? (isWin ? "active-win" : "active-lose") : ""}`} style={{ width: `${pct}%` }}>
          <span className="rm-dist-count">{count}</span>
        </div>
      </div>
    </div>
  );
}

function XpSection({ xpResult }) {
  const level = xpResult.level;
  const xpGained = xpResult.xp_gained || 0;
  const currentXp = level.current_xp;
  const levelXp = level.level_xp || 1;
  const previousXp = Math.max(0, currentXp - xpGained);
  const startPct = levelXp > 0 ? (previousXp / levelXp) * 100 : 0;
  const endPct = level.progress_pct;
  const [barWidth, setBarWidth] = useState(startPct);
  const [showGain, setShowGain] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setBarWidth(endPct), 400);
    const t2 = setTimeout(() => setShowGain(true), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [endPct]);
  return (
    <div className="rm-xp-section">
      <div className="rm-xp-header">
        <div className="rm-xp-level-badge"><span className="rm-xp-level-num">{level.level}</span></div>
        <div className="rm-xp-info">
          <div className="rm-xp-title-row">
            <span className="rm-xp-level-label">Level {level.level}</span>
            {showGain && xpGained > 0 && <span className="rm-xp-gain-badge">+{xpGained} XP</span>}
          </div>
          <div className="rm-xp-bar-wrap">
            <div className="rm-xp-bar-track">
              <div className="rm-xp-bar-fill" style={{ width: `${Math.min(barWidth, 100)}%` }} />
            </div>
            <span className="rm-xp-bar-text">{currentXp} / {levelXp} XP</span>
          </div>
          {xpResult.perfect_bonus > 0 && showGain && <span className="rm-xp-bonus">Perfect Day bonus: +{xpResult.perfect_bonus} XP</span>}
        </div>
      </div>
    </div>
  );
}

export default React.memo(function ResultModal({
  result, onClose, isGuest, onRegister,
  itemName, itemIcon, itemLabel = "spell",
  gameName = "spells", gameMode,
  answers = [], stats = null, xpResult = null,
}) {
  const isWin = result?.correct;
  const attemptNumber = result?.attempt_number;
  const [copied, setCopied] = useState(false);

  // Blokuj scroll body gdy modal jest otwarty
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const confetti = useMemo(() =>
    isWin ? Array.from({ length: 30 }, () => ({
      left: `${Math.random() * 100}%`, duration: `${0.8 + Math.random() * 1.2}s`,
      delay: `${Math.random() * 0.5}s`, round: Math.random() > 0.5,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    })) : [], [isWin]);

  const handleShare = useCallback(async () => {
    const text = buildShareText({ isWin, attemptNumber, gameMode, gameName, answers });
    try { await navigator.clipboard.writeText(text) } catch {
      const ta = document.createElement("textarea"); ta.value = text;
      ta.style.position = "fixed"; ta.style.left = "-9999px";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }, [isWin, attemptNumber, gameMode, gameName, answers]);

  const winRate = stats && stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : null;
  const avgAttempts = stats?.avg_attempts != null ? stats.avg_attempts : null;
  const distribution = stats?.distribution || [];
  const maxDist = Math.max(...distribution, 1);

  return (
    <div className="rm-overlay" onClick={onClose}>
      <div className={`rm-modal ${isWin ? "rm-modal--win" : "rm-modal--lose"}`} onClick={e => e.stopPropagation()}>
        {isWin && (
          <div className="rm-confetti" aria-hidden="true">
            {confetti.map((c, i) => (
              <div key={i} className="rm-confetti-piece" style={{ left: c.left, animationDuration: c.duration, animationDelay: c.delay, background: c.color, borderRadius: c.round ? "50%" : "2px" }} />
            ))}
          </div>
        )}
        <div className="rm-header">
          <span className="rm-emoji">{isWin ? "🏆" : "💀"}</span>
          <h2 className="rm-title">{isWin ? "You got it!" : "Better luck next time"}</h2>
          <p className="rm-subtitle">{isWin ? "Well played, champion of Azeroth!" : "Azeroth doesn't reveal its secrets so easily."}</p>
        </div>
        <div className="rm-divider" />
        <div className={`rm-spell-reveal${itemIcon ? "" : " rm-spell-reveal--no-icon"}`}>
          {itemIcon && <div className="rm-spell-icon-wrap"><img src={itemIcon} alt="" className="rm-spell-icon" /></div>}
          <div className="rm-spell-info">
            <span className="rm-spell-label">{isWin ? "You guessed" : `The ${itemLabel} was`}</span>
            <span className="rm-spell-name">{itemName || "???"}</span>
            {isWin && <span className="rm-spell-attempts">in <strong>{attemptNumber}</strong> {attemptNumber === 1 ? "attempt" : "attempts"}</span>}
          </div>
        </div>
        {xpResult && xpResult.level && !isGuest && (<><div className="rm-divider" /><XpSection xpResult={xpResult} /></>)}
        {stats && !isGuest && (
          <><div className="rm-divider" />
            <div className="rm-stats-section">
              <p className="rm-section-label">
                Your {gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Game"}
                {gameMode ? ` ${gameMode.charAt(0).toUpperCase() + gameMode.slice(1)}` : ""} Stats
              </p>
              <div className="rm-stats-row">
                <StatCard value={stats.played} label="Played" />
                <StatCard value={winRate !== null ? `${winRate}%` : "—"} label="Win Rate" />
                <StatCard value={avgAttempts !== null ? avgAttempts : "—"} label="Avg Attempts" />
              </div>
            </div>
          </>
        )}
        {distribution.length > 0 && (
          <><div className="rm-divider" />
            <div className="rm-dist-section">
              <p className="rm-section-label">
                Today's Results
                {stats?.total_today > 0 && <span className="rm-dist-total"> — {stats.total_today} player{stats.total_today !== 1 ? "s" : ""}</span>}
              </p>
              {distribution.map((count, i) => {
                const isLossBar = i === distribution.length - 1 && distribution.length > MAX_ATTEMPTS;
                return <DistributionBar key={i} attempt={isLossBar ? "💀️" : i + 1} count={count} maxCount={maxDist} isActive={isLossBar ? !isWin : (isWin && attemptNumber === i + 1)} isWin={isLossBar ? false : isWin} />;
              })}
            </div>
          </>
        )}
        <div className="rm-divider" />
        <div className="rm-actions">
          <button className="rm-btn rm-btn--close" onClick={onClose}>Close</button>
        </div>
        {isGuest && <button className="rm-register-btn" onClick={onRegister}>Register to unlock full potential!</button>}
      </div>
    </div>
  );
});